import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppConfig } from 'src/app/configs/app.config';
import { EventType } from 'src/app/constants/events-type';
import { EventManagerService } from 'src/app/core/services/event-manager/event-manager.service';
import { EventContent } from 'src/app/core/services/event-manager/models/event-content';

/** Delay so NVDA finishes the control that triggered preloader (WCAG 4.1.3). */
const PRELOADER_A11Y_ANNOUNCE_DELAY_MS = 120;
/** Clear-then-set gap so polite live regions detect a change (matches sort / ng-select pattern). */
const PRELOADER_A11Y_POKE_GAP_MS = 50;

@Component({
  selector: 'app-preloader',
  templateUrl: './preloader.component.html',
  styleUrls: ['./preloader.component.scss']
})
export class PreloaderComponent implements OnInit, OnDestroy {
  private subscription: Subscription | undefined;
  shouldShowPreloader = false;
  debounce: ReturnType<typeof setTimeout> | undefined;
  private preloaderAnnounceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private statusPokeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** Bound to the persistent polite live region in the template. */
  a11yStatusMessage = '';

  constructor(
    private eventManager: EventManagerService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.subscription = this.eventManager.subscribe(EventType.PreloaderShow, (eventContent: EventContent<boolean>) => {
      this.onPreloaderVisibility(eventContent);
    });
  }

  ngOnDestroy(): void {
    this.clearPreloaderAnnounceTimeout();
    this.clearStatusPokeTimeout();
    this.setPageBusy(false);
    if (this.subscription) {
      this.eventManager.destroy(this.subscription);
    }
  }

  private clearPreloaderAnnounceTimeout(): void {
    if (this.preloaderAnnounceTimeoutId != null) {
      clearTimeout(this.preloaderAnnounceTimeoutId);
      this.preloaderAnnounceTimeoutId = null;
    }
  }

  private clearStatusPokeTimeout(): void {
    if (this.statusPokeTimeoutId != null) {
      clearTimeout(this.statusPokeTimeoutId);
      this.statusPokeTimeoutId = null;
    }
  }

  /**
   * WCAG 4.1.3 — clear then set so NVDA announces updates on the always-mounted status region.
   */
  private pokeA11yStatusMessage(message: string): void {
    this.clearStatusPokeTimeout();
    this.a11yStatusMessage = '';
    this.statusPokeTimeoutId = setTimeout(() => {
      this.statusPokeTimeoutId = null;
      this.a11yStatusMessage = message;
    }, PRELOADER_A11Y_POKE_GAP_MS);
  }

  private setPageBusy(busy: boolean): void {
    if (busy) {
      document.body.setAttribute('aria-busy', 'true');
    } else {
      document.body.removeAttribute('aria-busy');
    }
  }

  private onPreloaderVisibility(eventContent: EventContent<boolean>): void {
    // Prevents further executution if there is no actual change
    if (this.shouldShowPreloader === eventContent.content) {
      return;
    }

    if (eventContent.content === false) {
      this.clearPreloaderAnnounceTimeout();
      clearTimeout(this.debounce);

      this.debounce = setTimeout(() => {
        this.shouldShowPreloader = eventContent.content;
        this.setPageBusy(false);
        this.pokeA11yStatusMessage(this.translate.instant('A11Y_CONTENT_LOADED'));
      }, AppConfig.Preloader.debounceTimeMs);
    } else {
      this.shouldShowPreloader = eventContent.content;
      this.setPageBusy(true);
      this.clearPreloaderAnnounceTimeout();
      this.preloaderAnnounceTimeoutId = setTimeout(() => {
        this.preloaderAnnounceTimeoutId = null;
        this.pokeA11yStatusMessage(this.translate.instant('A11Y_LOADING_PLEASE_WAIT'));
      }, PRELOADER_A11Y_ANNOUNCE_DELAY_MS);
    }
  }
}
